import React, { useEffect, useMemo } from 'react';
import { PriceComponent, Point } from '@/types';
import { PointBadge } from '../helpers/PointBadge';
import { useGetTSPsQuery } from '../services/edgeApi';
import { useSelector } from 'react-redux';
import { selectAllPoints } from '../map/slice/mapSelectors';
import { useTheme } from '../helpers/hooks';

export type PriceComponentFormProps = {
  priceComponent: PriceComponent | null;
  onSave: (component: Partial<PriceComponent>) => void;
  onCancel: () => void;
};

const BLANK_PRICE_COMPONENT: Partial<PriceComponent> = {
  id: 'new',
  description: '',
};

export const PriceComponentForm: React.FC<PriceComponentFormProps> = ({
  priceComponent,
  onSave,
  onCancel,
}) => {
  useGetTSPsQuery(undefined);
  const [theme] = useTheme();
  const allPoints = useSelector(selectAllPoints);

  const [payload, setPayload] = React.useState<Partial<PriceComponent>>(
    priceComponent ? { ...priceComponent } : BLANK_PRICE_COMPONENT,
  );

  const points = useMemo<Point[]>(
    () => priceComponent?.points.map((pointId) => allPoints[pointId]) ?? [],
    [allPoints, priceComponent],
  );

  const reset = () => {
    setPayload(priceComponent ? { ...priceComponent } : BLANK_PRICE_COMPONENT);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    const isChecked = (e.target as HTMLInputElement).checked ?? false;
    const value = isCheckbox ? isChecked : e.target.value;
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (priceComponent) {
      setPayload({ ...priceComponent });
    } else {
      setPayload(BLANK_PRICE_COMPONENT);
    }
  }, [priceComponent]);

  return (
    <div>
      <small className="text-muted">Required fields *</small>
      <hr />
      <form className="container d-grid gap-4">
        <div className="row">
          <div className="col-12">
            <label className="form-label" htmlFor="description">
              Description *
            </label>
            <input
              id="description"
              type="text"
              className="form-control"
              name="description"
              placeholder="Description"
              value={payload.description}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-5">
            <label className="form-label" htmlFor="points">
              Points
            </label>
            <div className="d-flex flex-column gap-2">
              {points.map((point) => (
                <PointBadge
                  key={point?.id.toString()}
                  point={point}
                  theme={theme}
                />
              ))}
              {points.length === 0 && (
                <span className="text-muted">No points assigned</span>
              )}
            </div>
          </div>
        </div>
      </form>
      <div className="mt-4 d-flex justify-content-end gap-3">
        <button className="btn btn-secondary" onClick={() => onSave(payload)}>
          Save
        </button>
        <button className="btn btn-secondary" onClick={reset}>
          Reset
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};
